import { recordCallFlow } from '../../../debug/call-flow-log'

/**
 * Callee ring timing for the "phone vibrates 1–2 s before the call screen"
 * report: INVITE received → ring requested (ringtone / vibration) → incoming
 * surface visible, per call. Native Android records the same timings for its
 * own surfaces (`native:android:incoming:screenShown`); both carry wall-clock
 * `ts`, so a hidden-WebView ring can be lined up against the native screen.
 *
 * A later INVITE for the same callId (room re-add) after RING_CYCLE_MS starts
 * a new cycle. In-memory and bounded.
 */
export const RING_CYCLE_MS = 60_000
const MAX_TRACKED = 32

type RingTiming = {
  inviteAtMs: number | null
  ringAtMs: number | null
  shown: Set<string>
  suppressed: Set<string>
}

const timings = new Map<string, RingTiming>()

function track(callId: string, timing: RingTiming): RingTiming {
  if (!timings.has(callId) && timings.size >= MAX_TRACKED) {
    const oldest = timings.keys().next().value
    if (oldest) timings.delete(oldest)
  }
  timings.set(callId, timing)
  return timing
}

export function noteIncomingInviteReceived(callId: string, nowMs: number = Date.now()): void {
  if (!callId) return
  const existing = timings.get(callId)
  if (existing?.inviteAtMs != null && nowMs - existing.inviteAtMs < RING_CYCLE_MS) return
  track(callId, emptyTiming(nowMs))
}

function emptyTiming(inviteAtMs: number | null): RingTiming {
  return { inviteAtMs, ringAtMs: null, shown: new Set(), suppressed: new Set() }
}

/** Pure: ms since `atMs`; -1 when it never happened. */
export function sinceMs(atMs: number | null | undefined, nowMs: number): number {
  return atMs == null ? -1 : Math.max(0, nowMs - atMs)
}

/** The JS ring (ringtone + vibration pulse) was requested for this call. */
export function noteIncomingRingStarted(callId: string, nowMs: number = Date.now()): void {
  const timing = callId ? timings.get(callId) : undefined
  if (!timing || timing.ringAtMs != null) return
  timing.ringAtMs = nowMs
  recordCallFlow(
    'ctrl:incoming:ringStarted',
    { sinceInviteMs: sinceMs(timing.inviteAtMs, nowMs) },
    { callId },
  )
}

/**
 * The JS ring stood down for this call (`os-ring`: CallKit / Telecom rings
 * instead; `ringtone-disabled`: the user turned the call ringtone off). Once
 * per call and reason. Ringer-mode silencing inside the ring player itself is
 * not visible here.
 */
export function noteIncomingRingSuppressed(
  callId: string,
  reason: string,
  nowMs: number = Date.now(),
): void {
  if (!callId) return
  const timing = timings.get(callId) ?? track(callId, emptyTiming(null))
  if (timing.suppressed.has(reason)) return
  timing.suppressed.add(reason)
  recordCallFlow(
    'ctrl:incoming:ringSuppressed',
    { reason, sinceInviteMs: sinceMs(timing.inviteAtMs, nowMs) },
    { callId },
  )
}

/**
 * An incoming-call surface became visible. Once per call and surface; returns
 * whether the event was recorded.
 */
export function noteIncomingScreenShown(
  callId: string,
  surface: string,
  nowMs: number = Date.now(),
): boolean {
  if (!callId) return false
  const timing = timings.get(callId) ?? track(callId, emptyTiming(null))
  if (timing.shown.has(surface)) return false
  timing.shown.add(surface)
  recordCallFlow(
    'ctrl:incoming:screenShown',
    {
      surface,
      sinceInviteMs: sinceMs(timing.inviteAtMs, nowMs),
      sinceRingMs: sinceMs(timing.ringAtMs, nowMs),
    },
    { callId },
  )
  return true
}

export function resetIncomingRingTimingForTests(): void {
  timings.clear()
}
